import { Component, ElementRef, OnInit } from "@angular/core";
import { Cotista } from "src/app/model/cotista.model";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MensagemService } from "src/app/features/mensagem.service";
import { Router } from "@angular/router";
import { DatePipe } from "@angular/common";
import { ConglomeradoService } from "../../conglomerado.service";
import { ParticipanteConglomerado } from "src/app/model/participante.conglomerado.model";
import { TipoCotistaEnum } from "src/app/model/tipo-cotista-enum";
import { NovasSubscricoesDTO, SubscricaoService } from "../../subscricao.service";

export interface SubscricaoTableDto {
  id?: number;
  numeroAporte: number;
  data: Date;
  valor: number;
  categoria: string;
}

@Component({
    selector: 'app-nova-subscricao-cota',
    templateUrl: './nova-subscricao-cota.component.html',
    styleUrls: ['./nova-subscricao-cota.component.scss'],
    standalone: false
})
export class NovaSubscricaoCotaComponent implements OnInit {
  cotistaSelecionado: Cotista;

  categorias = [];
  subscricoes: SubscricaoTableDto[] = [];
  dataHoje = new Date();
  subscricaoForm: FormGroup;
  participantesConglomerado: ParticipanteConglomerado[] = [];

  constructor(
    private subscricaoService: SubscricaoService,
    formBuilder: FormBuilder,
    private router: Router,
    private mensagemService: MensagemService,
    private el: ElementRef,
    private datePipe: DatePipe,
    private conglomeradoService: ConglomeradoService) {
    this.subscricaoForm = formBuilder.group({
      data: [null, Validators.required],
      valor: [null, [Validators.required, Validators.min(0.01)]],
      categoria: [null, Validators.required],
    });
  }

  ngOnInit(): void {
    this.carregarCategorias();
  }

  async carregarCategorias() {
    this.categorias = await this.subscricaoService.getCategorias().toPromise();
  }

  async carregarParticipantesConglomerado() {
    if (this.cotistaSelecionado && this.cotistaSelecionado.tipoCotista === TipoCotistaEnum.AGENTE_FINANCEIRO) {
      this.participantesConglomerado = await this.conglomeradoService
        .buscarAtivosPorAgente(this.cotistaSelecionado.id).toPromise();
    }
  }

  async carregarSubscricoes() {
    if (this.cotistaSelecionado) {
      const subscricoesTmp = await this.subscricaoService
      .getSubscricoesPorCotistaEModalidade(this.cotistaSelecionado.id, this.cotistaSelecionado.tipoCotista)
      .toPromise();
      this.subscricoes = subscricoesTmp.map(sub => {
        return {
          id: sub.id,
          numeroAporte: sub.numeroCota,
          data: sub.dataSubscricao,
          valor: sub.valor,
          categoria: sub.categoriaCota
        } as SubscricaoTableDto;
      });
    }
  }

  selecionaCotista(cotista) {
    this.cotistaSelecionado = cotista;
    this.carregarSubscricoes();
    if (cotista == null) {
      this.subscricoes = [];
    }
    this.subscricaoForm.reset();
    this.carregarParticipantesConglomerado();
  }

  incluirSubscricao() {
    if (this.subscricaoForm.invalid) {
      this.subscricaoForm.markAllAsTouched();
      return;
    }
    const {
      data,
      valor,
      categoria
    } = this.subscricaoForm.value;

    const subscriscoesJaSalvas = this.subscricoes.filter(s => s.id != null);

    if (subscriscoesJaSalvas
      .some(s => s.categoria === categoria && new Date(this.datePipe.transform(s.data, 'yyyy-MM-dd')).getTime() > data.getTime())) {
      this.mensagemService.adicionarMensagemErro('Subscrição não pode estar fora da ordem cronológica');
      return;
    }

    const novaSubscricao: SubscricaoTableDto = {
      id: null,
      numeroAporte: 1,
      data,
      valor,
      categoria
    }

    this.subscricoes.push(novaSubscricao);
    this.ordenaEAdequaNumerosAportes();
    this.subscricaoForm.reset();
    setTimeout(() => {
      this.removeNgInvalidInput('data-subscricao');
    }, 100);
  }

  removeNgInvalidInput(id: string){
    const inputElement = this.el.nativeElement.querySelector(`#${id}`);
    inputElement.classList.remove('ng-invalid');
  }

  ordenaEAdequaNumerosAportes() {
    const subscriscoesJaSalvas = this.subscricoes.filter(s => s.id != null);
    const novasSubscricoes = this.subscricoes.filter(s => !subscriscoesJaSalvas.some(s1 => s.id === s1.id));

    novasSubscricoes.sort((a, b) => a.data.getTime() - b.data.getTime())
    novasSubscricoes.forEach(a => a.numeroAporte = 0);
    let index = 0;
    do {
      const sub = novasSubscricoes[index];
      let maiorAporteCategoria = this.calculaMaiorAportePorCategoria(subscriscoesJaSalvas, sub.categoria);
      const maiorAporteCategoriaNova = this.calculaMaiorAportePorCategoria(novasSubscricoes, sub.categoria);
      if (maiorAporteCategoria != maiorAporteCategoriaNova && maiorAporteCategoriaNova !== 0) {
        maiorAporteCategoria = maiorAporteCategoriaNova;
      }
      novasSubscricoes[index].numeroAporte = (maiorAporteCategoria + 1);
      index += 1;
    } while (index < novasSubscricoes.length);

    this.subscricoes = [...subscriscoesJaSalvas, ...novasSubscricoes];
  }

  calculaMaiorAportePorCategoria(subscricoes, categoria) {
    const subscricoesFiltradasPorCategoria = subscricoes.filter(s => s.categoria === categoria);
    let maior = 0;
    if (subscricoesFiltradasPorCategoria.length > 0) {
      maior = Number(subscricoesFiltradasPorCategoria[0].numeroAporte);
    }
    for (let sub of subscricoesFiltradasPorCategoria) {
      const numeroAporte = Number(sub.numeroAporte);
      if (maior < numeroAporte) {
        maior = numeroAporte;
      }
    }
    return maior;
  }

  remover(index: number) {
    this.subscricoes.splice(index, 1);
    this.ordenaEAdequaNumerosAportes();
  }

  temSubscricoesNovas() {
    return this.subscricoes.some(s => s.id === null);
  }

  async salvar() {
    if (!this.temSubscricoesNovas()) {
      return;
    }

    const subscricoes = this.subscricoes
      .filter(s => s.id === null)
      .map(s => {
        return {
          numeroAporte: Number(s.numeroAporte),
          data: s.data,
          valor: Number(s.valor),
          categoria: s.categoria,
        }
      });

    const novasSubscricoes: NovasSubscricoesDTO = {
      idCotista: this.cotistaSelecionado.id,
      tipoCotista: this.cotistaSelecionado.tipoCotista,
      subscricoes,
    }
    try {
      await this.subscricaoService.salvar(novasSubscricoes).toPromise();
      this.mensagemService.adicionarMensagemSucesso('Subscrição de Cotas criadas/atualizadas com sucesso.');
      this.irParaPesquisa();
    } catch (err) {
      console.log('Erro ao salvar subscrições', err);
      this.mensagemService.adicionarMensagemErro('Erro ao salvar as subscrições');
    }
  }

  cancelarCadastro() {
    this.irParaPesquisa();
  }

  irParaPesquisa() {
    this.router.navigate(['habilitacao', 'subscricao']);
  }

  get exibirListaParticipantesConglomerado(): boolean {
    return this.participantesConglomerado && this.participantesConglomerado.length > 0
  }
}
